import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaFilter, FaPlus } from "react-icons/fa";
import { getAllActiveProvider, getOrganisation } from "../../../config/LiveApi";
import "./ProviderTable.css";
import AddPayinModal from "./AddPayinModal";
import AddPayoutModal from "./AddPayoutModal";
import ProviderModal from "../SuccessModal/ProviderModal";
import UserModal from "../SuccessModal/UserModal";
import ProviderBalanceModal from "../SuccessModal/ProviderBalanceModal";
import UserPayoutSuccessModal from "../SuccessModal/UserPayoutSuccessModal";
import DataTable from "../../../components/common/DataTable/DataTable";

const Table = () => {
  const token = useSelector((store) => store.userReducer.token);
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [modalType, setModalType] = useState(null);

  // Filters
  const [selectedOrgName, setSelectedOrgName] = useState("All");
  const [orgOptions, setOrgOptions] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");

  // 🔎 Search state with debounce
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [providerModal, setProviderModal] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [providerBlanceModal, setProviderBlanceModal] = useState(false);
  const [userPayoutSuccessModal, setUserPayoutSuccessModal] = useState(false);

  useEffect(() => {
    fetchAllProvider();
  }, []);

  const fetchAllProvider = async () => {
    setIsLoading(true);
    try {
      const response = await getAllActiveProvider(token);
      setProvider(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch providers.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = (providerId, type) => {
    setSelectedProvider(providerId);
    setModalType(type);
    setShowModal(true);
  };

  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const response = await getOrganisation(token);
        const orgData = response.data;
        setOrgOptions(orgData);
      } catch (err) {
        console.error("Error fetching organisations:", err.message);
        toast.error("Unable to fetch organisations.");
      }
    };

    fetchOrganisations();
  }, [token]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const renderMerchantList = (merchants, showAddIcon, providerId, type) => {
    return (
      <div className="merchant-list">
        {(!merchants || merchants.length === 0) && (
          <span className="merchant-empty">-</span>
        )}

        {merchants &&
          merchants.map((u) => (
            <span key={u.id} className="merchant-badge">
              {u?.alias || u?.id || ""}
            </span>
          ))}

        {showAddIcon && (
          <button
            className="merchant-add-btn"
            onClick={() => handleAddClick(providerId, type.toLowerCase())}
          >
            <FaPlus />
          </button>
        )}
      </div>
    );
  };

  // ✅ Apply filters (Org + Type + Search)
  const filteredProviders = provider.filter((prov) => {
    let orgMatch = true;
    let typeMatch = true;
    let searchMatch = true;

    if (selectedOrgName && selectedOrgName !== "All") {
      orgMatch = prov.orgName === selectedOrgName;
    }

    if (typeFilter) {
      if (typeFilter === "both") {
        typeMatch = prov.type === "both";
      } else {
        typeMatch = prov.type === typeFilter;
      }
    }

    if (debouncedSearchTerm) {
      searchMatch = prov.providerName
        .toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase());
    }

    return orgMatch && typeMatch && searchMatch;
  });

  const columns = [
    {
      key: "providerName",
      header: "Provider Name",
    },

    {
      key: "orgName",
      header: "Org Name",
      filterable: true,
      filterType: "select",
      filterValue: selectedOrgName,
      filterOptions: [
        { label: "All", value: "All" },
        ...orgOptions.map((org) => ({
          label: org.orgName,
          value: org.orgName,
        })),
      ],
      onFilterChange: setSelectedOrgName,
    },

    {
      key: "type",
      header: "Type",
      filterable: true,
      filterType: "menu",
      filterValue: typeFilter,
      filterOptions: [
        { label: "All", value: "" },
        { label: "Payin", value: "payin" },
        { label: "Payout", value: "payout" },
        { label: "Both", value: "both" },
      ],
      onFilterChange: setTypeFilter,
    },

    {
      key: "payinLimit",
      header: "Payin (Limit / Used)",
      render: (row) => {
        if (!(row.type === "payin" || row.type === "both")) {
          return "-";
        }

        const used = row.providerLimitUsed?.payin || 0;
        const limit = row.providerLimit?.payin || 0;
        const usagePercent = limit > 0 ? (used / limit) * 100 : 0;

        return (
          <>
            <span
              className={
                usagePercent >= 90 ? "used-limit-red" : "used-limit-green"
              }
            >
              {used.toLocaleString()}
            </span>
            {" / "}
            {limit.toLocaleString()}
          </>
        );
      },
    },

    {
      key: "payoutLimit",
      header: "Payout (Limit / Used)",
      render: (row) => {
        if (!(row.type === "payout" || row.type === "both")) {
          return "-";
        }

        const used = row.providerLimitUsed?.payout || 0;
        const limit = row.providerLimit?.payout || 0;
        const usagePercent = limit > 0 ? (used / limit) * 100 : 0;

        return (
          <>
            <span
              className={
                usagePercent >= 90 ? "used-limit-red" : "used-limit-green"
              }
            >
              {used.toLocaleString()}
            </span>
            {" / "}
            {limit.toLocaleString()}
          </>
        );
      },
    },

    {
      key: "payin",
      header: "Payin Merchant",
      render: (row) =>
        renderMerchantList(
          row.payin,
          row.type === "payin" || row.type === "both",
          row.id,
          "Payin",
        ),
      wrapCell: true,
    },

    {
      key: "payout",
      header: "Payout Merchant",
      render: (row) =>
        renderMerchantList(
          row.payout,
          row.type === "payout" || row.type === "both",
          row.id,
          "Payout",
        ),
      wrapCell: true,
    },
  ];

  return (
    <div className="provider-report-table-container">
      <div className="provider-report-table-header">
        <div>
          <h2 className="provider-report-table-title">All Active Providers</h2>
          <button
            className="provider-success-rate-buttton"
            onClick={() => setProviderModal(true)}
          >
            Provider Success Rate
          </button>
          <button
            className="provider-success-rate-buttton-1"
            onClick={() => setUserModal(true)}
          >
            User Success Rate
          </button>
          <button
            className="provider-success-rate-buttton-1"
            onClick={() => setUserPayoutSuccessModal(true)}
          >
            User Payout Success Rate
          </button>
          <button
            className="provider-success-rate-buttton-2"
            onClick={() => setProviderBlanceModal(true)}
          >
            Provider Balance
          </button>
        </div>

        <input
          className="provider-key-input-field-container"
          type="text"
          placeholder="Enter Provider Name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredProviders}
        loading={isLoading}
        emptyMessage="No Active Provider found."
      />

      {showModal && modalType === "payin" && (
        <AddPayinModal
          providerId={selectedProvider}
          onClose={() => setShowModal(false)}
          onSuccess={fetchAllProvider}
        />
      )}

      {showModal && modalType === "payout" && (
        <AddPayoutModal
          providerId={selectedProvider}
          onClose={() => setShowModal(false)}
          onSuccess={fetchAllProvider}
        />
      )}
      {providerModal && <ProviderModal setShowModal={setProviderModal} />}
      {userModal && <UserModal setShowModal={setUserModal} />}
      {providerBlanceModal && (
        <ProviderBalanceModal setShowModal={setProviderBlanceModal} />
      )}
      {userPayoutSuccessModal && (
        <UserPayoutSuccessModal setShowModal={setUserPayoutSuccessModal} />
      )}
    </div>
  );
};

export default Table;
